import { useUser } from '@clerk/react';
import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { RoutesList } from '../config/routes-list';
import { AnalyticsService } from '../services/analytics/analytics.service';
import type { IAnalyticsService } from '../services/analytics/analytics.service.interface';
import {
  readAnonymousConsent,
  writeAnonymousConsent,
} from '../services/analytics/anonymous-consent';

/**
 * Pre-login cookie banner for advertising pixels. Visitors landing from a
 * Meta/TikTok campaign hit accounts.prospero.food without an account, so the
 * website's own banner choice isn't reachable across subdomains and the
 * marketing pixels need an explicit choice before they may load. Logged-in
 * users manage the same categories through accept-terms/settings instead.
 */
export function AnonymousCookieBar() {
  const { t } = useTranslation();
  const analytics = useInjection<IAnalyticsService>(AnalyticsService);
  const { isLoaded, isSignedIn } = useUser();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isLoaded || isSignedIn) {
      return;
    }
    // Wait a tick so the app shell paints first; skipping signed-in users is
    // what makes this safe next to the accept-terms interstitial.
    const timer = setTimeout(() => {
      setVisible(readAnonymousConsent() === null);
    }, 600);
    return () => clearTimeout(timer);
  }, [isLoaded, isSignedIn]);

  if (!visible) {
    return null;
  }

  const decide = (marketingGranted: boolean) => {
    writeAnonymousConsent(true, marketingGranted);
    // Service re-reads localStorage (and any cached server prefs) internally.
    analytics.reapplyConsents();
    setVisible(false);
  };

  return (
    <Paper
      role="dialog"
      aria-label={t('cookieBar.title')}
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 'calc(var(--safe-area-bottom) + 12px)',
        left: 12,
        right: 12,
        zIndex: (theme) => theme.zIndex.snackbar - 1,
        p: 2,
        borderRadius: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.5,
        alignItems: { sm: 'center' },
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('cookieBar.title')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('cookieBar.description')}{' '}
          <Link component={RouterLink} to={RoutesList.PRIVACY} sx={{ verticalAlign: 'baseline' }}>
            {t('cookieBar.privacyLink')}
          </Link>
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" onClick={() => decide(false)}>
          {t('cookieBar.reject')}
        </Button>
        <Button size="small" variant="contained" onClick={() => decide(true)}>
          {t('cookieBar.accept')}
        </Button>
      </Box>
    </Paper>
  );
}
